import React, { useState, useEffect } from 'react';
import { reviewsAPI } from '../../services/apiService';
import { Edit, Trash2, Star, Plus, Filter, Check, X } from 'lucide-react';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    service: '',
    text_de: '',
    text_ru: '',
    approved: false
  });

  useEffect(() => {
    loadReviews();
  }, [filterStatus]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await reviewsAPI.getAll();
      let filteredReviews = response.data || [];
      
      if (filterStatus === 'approved') {
        filteredReviews = filteredReviews.filter(review => review.approved);
      } else if (filterStatus === 'pending') {
        filteredReviews = filteredReviews.filter(review => !review.approved);
      }
      
      setReviews(filteredReviews);
    } catch (error) {
      console.error('Ошибка загрузки отзывов:', error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = () => {
    setEditingReview(null);
    setFormData({
      name: '',
      email: '',
      rating: 5,
      service: '',
      text_de: '',
      text_ru: '',
      approved: false
    });
    setShowForm(true);
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setFormData({
      name: review.name || '',
      email: review.email || '',
      rating: review.rating || 5,
      service: review.service || '',
      text_de: review.text_de || '',
      text_ru: review.text_ru || '',
      approved: review.approved || false
    });
    setShowForm(true);
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот отзыв?')) {
      try {
        await reviewsAPI.delete(reviewId);
        await loadReviews();
      } catch (error) {
        console.error('Ошибка удаления отзыва:', error);
        alert('Ошибка при удалении отзыва');
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReview) {
        await reviewsAPI.update(editingReview.id, formData);
      } else {
        await reviewsAPI.create(formData);
      }
      setShowForm(false);
      await loadReviews();
    } catch (error) {
      console.error('Ошибка сохранения отзыва:', error);
      alert('Ошибка при сохранении отзыва');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingReview(null);
    setFormData({
      name: '',
      email: '',
      rating: 5,
      service: '',
      text_de: '',
      text_ru: '',
      approved: false
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-400'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление отзывами</h1>
          <p className="text-gray-300">Просмотр и модерация отзывов клиентов</p>
        </div>
        <button
          onClick={handleCreateReview}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
        >
          <Plus className="h-5 w-5 mr-2" />
          Добавить отзыв
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">Все отзывы</option>
            <option value="approved">Одобренные</option>
            <option value="pending">Ожидающие модерации</option>
          </select>
          <div className="text-sm text-gray-300">
            {reviews.length} {reviews.length === 1 ? 'отзыв' : reviews.length < 5 ? 'отзыва' : 'отзывов'}
          </div>
        </div>
      </div>

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Star className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-xl text-white mb-2">Отзывов пока нет</h3>
          <p className="text-gray-400 mb-6">Создайте свой первый отзыв или дождитесь отзывов от клиентов</p>
          <button
            onClick={handleCreateReview}
            className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
          >
            <Plus className="h-5 w-5 mr-2" />
            Создать первый отзыв
          </button>
        </div>
      ) : (
        <div className="grid gap-6">
          {reviews.map((review) => (
            <div key={review.id} className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-3">
                    <h3 className="text-lg font-semibold text-white">{review.name}</h3>
                    <div className="flex items-center">
                      {renderStars(review.rating)}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      review.approved 
                        ? 'bg-green-900 text-green-300' 
                        : 'bg-yellow-900 text-yellow-300'
                    }`}>
                      {review.approved ? 'Одобрен' : 'На модерации'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-gray-300">
                      <span className="font-medium">🇩🇪:</span> {review.text_de}
                    </p>
                    <p className="text-gray-300">
                      <span className="font-medium">🇷🇺:</span> {review.text_ru}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-400">
                    <span>Email: {review.email}</span>
                    <span>Услуга: {review.service}</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleEditReview(review)}
                    className="text-blue-400 hover:text-blue-300 p-2"
                    title="Редактировать"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteReview(review.id)}
                    className="text-red-400 hover:text-red-300 p-2"
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-600">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">
                {editingReview ? 'Редактировать отзыв' : 'Создать новый отзыв'}
              </h2>
              
              <form onSubmit={handleFormSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Имя клиента *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Введите имя клиента..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                {/* Rating and Service */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Рейтинг *
                    </label>
                    <select
                      name="rating"
                      value={formData.rating}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value={5}>5 звезд</option>
                      <option value={4}>4 звезды</option>
                      <option value={3}>3 звезды</option>
                      <option value={2}>2 звезды</option>
                      <option value={1}>1 звезда</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Услуга
                    </label>
                    <input
                      type="text"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Например: Свадебная фотография"
                    />
                  </div>
                </div>

                {/* Review Text */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Отзыв (немецкий) *
                    </label>
                    <textarea
                      name="text_de"
                      value={formData.text_de}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Текст отзыва на немецком..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Отзыв (русский) *
                    </label>
                    <textarea
                      name="text_ru"
                      value={formData.text_ru}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Текст отзыва на русском..."
                    />
                  </div>
                </div>

                {/* Approval */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="approved"
                    name="approved"
                    checked={formData.approved}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 border-gray-600 bg-gray-700 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="approved" className="ml-2 text-sm text-gray-300">
                    Одобрить отзыв для показа на сайте
                  </label>
                </div>

                {/* Submit */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700"
                  >
                    Отменить
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    {editingReview ? 'Обновить отзыв' : 'Создать отзыв'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;